/** Maps animation progress 0…1 to eased progress; may leave 0…1 on an overshooting curve. */
export type Easing = (x: number) => number;

const NEWTON_ITERATIONS = 8;
const EPSILON = 1e-7;

/**
 * The CSS `cubic-bezier(x1, y1, x2, y2)` timing function, solved the way browsers do it (WebKit `UnitBezier.h`):
 * find the curve parameter whose x is the progress — Newton's method first, bisection when the slope is too flat —
 * then read y there. The ends are exact: 0 → 0, 1 → 1.
 */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): Easing {
  // Polynomial coefficients of x(t) and y(t), the curve from (0,0) to (1,1).
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;

  const curveX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const curveY = (t: number) => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;

  const solveT = (x: number) => {
    let t = x;
    for (let i = 0; i < NEWTON_ITERATIONS; i++) {
      const error = curveX(t) - x;
      if (Math.abs(error) < EPSILON) return t;
      const slope = slopeX(t);
      if (Math.abs(slope) < 1e-6) break;
      t -= error / slope;
    }
    // x(t) never decreases for valid CSS control points, so bisection always keeps the answer bracketed.
    let lo = 0;
    let hi = 1;
    t = x;
    while (hi - lo > EPSILON) {
      const value = curveX(t);
      if (Math.abs(value - x) < EPSILON) return t;
      if (value < x) lo = t;
      else hi = t;
      t = (lo + hi) / 2;
    }
    return t;
  };

  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    return curveY(solveT(x));
  };
}

export const linear: Easing = (x) => Math.min(1, Math.max(0, x));
/** CSS `ease`. */
export const ease = cubicBezier(0.25, 0.1, 0.25, 1);
/** CSS `ease-out`. */
export const easeOut = cubicBezier(0, 0, 0.58, 1);
/** CSS `ease-in-out`. */
export const easeInOut = cubicBezier(0.42, 0, 0.58, 1);

// Curves of the built-in video sets (Material Motion, https://m1.material.io/motion/duration-easing.html).
/** Entrances, 0.6–0.9 s: fast start, long settle. */
export const emphasizedDecelerate = cubicBezier(0.05, 0.7, 0.1, 1);
/** Exits, 0.3–0.4 s: slow start, leaves at speed. */
export const emphasizedAccelerate = cubicBezier(0.3, 0, 0.8, 0.15);
/** Overshoot past the target and back — the «отскок» of slams and pop-ins. */
export const backOut = cubicBezier(0.34, 1.56, 0.64, 1);
